import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  CssBaseline,
  Divider,
  Paper,
  Stack,
  TextField,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import DashboardIcon from "@mui/icons-material/SpaceDashboard";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { submitPartnerRequest } from "../api/partnerRequestService";
import { createLandingTheme, brandColors } from "./LandingPage.theme";
import { LANGUAGES, translations } from "./LandingPage.i18n";

const initialForm = {
  fullName: "",
  phone: "",
  email: "",
  businessName: "",
  city: "",
  message: "",
};

const FEATURE_ICONS = [StorefrontIcon, DashboardIcon, LocalShippingIcon];

export default function LandingPage() {
  const [lang, setLang] = useState("en");
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);

  const langMeta = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];
  const t = translations[lang];
  const theme = useMemo(
    () => createLandingTheme(langMeta.dir, lang),
    [langMeta.dir, lang],
  );

  useEffect(() => {
    const prevLang = document.documentElement.lang;
    const prevDir = document.documentElement.dir;
    document.documentElement.lang = lang;
    document.documentElement.dir = langMeta.dir;
    return () => {
      document.documentElement.lang = prevLang;
      document.documentElement.dir = prevDir;
    };
  }, [lang, langMeta.dir]);

  const isValid =
    form.fullName.trim().length > 0 && form.phone.trim().length > 0;

  function handleChange(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  function scrollToForm() {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid || submitting) return;

    setSubmitting(true);
    setError("");
    try {
      await submitPartnerRequest({
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        businessName: form.businessName.trim() || undefined,
        city: form.city.trim() || undefined,
        message: form.message.trim() || undefined,
      });
      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      setError(
        typeof err.response?.data === "string"
          ? err.response.data
          : t.genericError,
      );
    } finally {
      setSubmitting(false);
    }
  }

  const features = [
    { title: t.feature1Title, desc: t.feature1Desc },
    { title: t.feature2Title, desc: t.feature2Desc },
    { title: t.feature3Title, desc: t.feature3Desc },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box dir={langMeta.dir} sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
        {/* Header */}
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Container maxWidth="lg">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ py: 1.75 }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  component="img"
                  src="/apple-touch-icon.png"
                  alt="Salatty"
                  sx={{ width: 38, height: 38, borderRadius: "10px" }}
                />
                <Stack spacing={-0.25}>
                  <Typography variant="subtitle1" fontWeight={800} color={brandColors.ink}>
                    {t.brand}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.brandSuffix}
                  </Typography>
                </Stack>
              </Stack>

              <ToggleButtonGroup
                value={lang}
                exclusive
                size="small"
                onChange={(e, value) => value && setLang(value)}
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 3,
                  p: 0.5,
                  border: "1px solid",
                  borderColor: "divider",
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: 2.5,
                    px: 1.5,
                    py: 0.5,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "text.secondary",
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: "#fff",
                      "&:hover": { bgcolor: "primary.dark" },
                    },
                  },
                }}
              >
                {LANGUAGES.map((l) => (
                  <ToggleButton key={l.code} value={l.code}>
                    {translations[l.code].languageName}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          </Container>
        </Box>

        {/* Hero */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${brandColors.emeraldLight} 0%, ${brandColors.emeraldDark} 100%)`,
            pt: { xs: 6, md: 8 },
            pb: { xs: 10, md: 12 },
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
                gap: { xs: 5, md: 6 },
                alignItems: "center",
              }}
            >
              {/* Copy */}
              <Stack spacing={2.5} sx={{ textAlign: { xs: "center", md: "start" } }}>
                <Chip
                  icon={<AutoAwesomeIcon sx={{ color: `${brandColors.amber} !important` }} />}
                  label={t.heroBadge}
                  sx={{
                    alignSelf: { xs: "center", md: "start" },
                    bgcolor: "rgba(255, 255, 255, 0.16)",
                    color: "#fff",
                    fontWeight: 700,
                    border: "1px solid rgba(255,255,255,0.25)",
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{ color: "#fff", fontSize: { xs: 30, sm: 36, md: 42 }, lineHeight: 1.2 }}
                >
                  {t.heroTitle}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    maxWidth: 520,
                    mx: { xs: "auto", md: 0 },
                    fontSize: { xs: 15, md: 17 },
                  }}
                >
                  {t.heroSubtitle}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ justifyContent: { xs: "center", md: "start" }, pt: 1 }}
                >
                  <Button
                    onClick={scrollToForm}
                    variant="contained"
                    size="large"
                    endIcon={<ArrowDownwardIcon />}
                    sx={{
                      bgcolor: "#fff",
                      color: brandColors.emeraldDark,
                      px: 3.5,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                    }}
                  >
                    {t.heroCta}
                  </Button>
                </Stack>
              </Stack>

              {/* Highlights panel */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 3.5 },
                  borderRadius: 4,
                  boxShadow: "0 20px 48px rgba(6, 34, 29, 0.25)",
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.6 }}
                >
                  {t.highlightsEyebrow}
                </Typography>
                <Stack divider={<Divider sx={{ my: 2 }} />} sx={{ mt: 1 }}>
                  {features.map((feature, idx) => {
                    const Icon = FEATURE_ICONS[idx];
                    return (
                      <Stack key={feature.title} direction="row" spacing={2} alignItems="flex-start">
                        <Box
                          sx={{
                            flexShrink: 0,
                            width: 44,
                            height: 44,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: `linear-gradient(135deg, ${brandColors.tealLight} 0%, ${brandColors.tealDark} 100%)`,
                          }}
                        >
                          <Icon sx={{ color: "#fff", fontSize: 22 }} />
                        </Box>
                        <Stack spacing={0.25}>
                          <Typography variant="subtitle1" fontWeight={700} color={brandColors.ink}>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {feature.desc}
                          </Typography>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </Paper>
            </Box>
          </Container>
        </Box>

        {/* Form */}
        <Container maxWidth="sm" sx={{ mt: { xs: -6, md: -7 }, position: "relative", pb: 8 }}>
          <Paper
            ref={formRef}
            elevation={0}
            sx={{
              p: { xs: 3, md: 4.5 },
              borderRadius: 4,
              boxShadow: "0 16px 40px rgba(11, 43, 38, 0.1)",
              border: "1px solid",
              borderColor: "divider",
              scrollMarginTop: 24,
            }}
          >
            <Stack spacing={0.5} sx={{ mb: 3, textAlign: "center" }}>
              <Typography variant="h5" color={brandColors.ink}>
                {t.formTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t.formSubtitle}
              </Typography>
            </Stack>

            {submitted ? (
              <Alert severity="success" onClose={() => setSubmitted(false)}>
                {t.successMessage}
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  {error && <Alert severity="error">{error}</Alert>}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2.5,
                    }}
                  >
                    <TextField
                      label={t.fullNameLabel}
                      value={form.fullName}
                      onChange={handleChange("fullName")}
                      required
                      fullWidth
                      autoFocus
                    />
                    <TextField
                      label={t.phoneLabel}
                      value={form.phone}
                      onChange={handleChange("phone")}
                      required
                      fullWidth
                    />
                    <TextField
                      label={t.emailLabel}
                      type="email"
                      value={form.email}
                      onChange={handleChange("email")}
                      fullWidth
                    />
                    <TextField
                      label={t.businessNameLabel}
                      value={form.businessName}
                      onChange={handleChange("businessName")}
                      fullWidth
                    />
                    <Box sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}>
                      <TextField
                        label={t.cityLabel}
                        value={form.city}
                        onChange={handleChange("city")}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}>
                      <TextField
                        label={t.messageLabel}
                        value={form.message}
                        onChange={handleChange("message")}
                        multiline
                        minRows={3}
                        fullWidth
                        helperText={t.messageHelper}
                      />
                    </Box>
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!isValid || submitting}
                    sx={{
                      background: `linear-gradient(135deg, ${brandColors.tealLight} 0%, ${brandColors.emeraldDark} 100%)`,
                      "&:hover": {
                        background: `linear-gradient(135deg, ${brandColors.emeraldLight} 0%, ${brandColors.tealDark} 100%)`,
                      },
                    }}
                  >
                    {submitting ? t.submittingLabel : t.submitLabel}
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center", pt: 4 }}
          >
            © {new Date().getFullYear()} {t.footerText}
          </Typography>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
